import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    X, FileText, ExternalLink, Calendar, User, AlertCircle, Copy, CheckCheck, Link2,
    ShieldCheck, Clock, MinusCircle, ClipboardCheck, RefreshCw, Loader2, MailWarning, Users
} from 'lucide-react';
import { descreverHistoricoTermos, emailNaoEntregue, type StatusTermo } from '../../../../src/lib/termoValido';
import { supabase } from '../../../../src/lib/supabase';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { logUsageEvent } from '../../../../src/lib/logger';
import { useToast } from '../../../../src/contexts/ToastContext';
import { propsFundoModal } from '../../../../src/lib/backdropModal';

interface TermHistoryItem {
    url_pdf: string;
    data_criacao: string;
    tipo_cliente?: string;
    numero_termo?: string;
    validado?: boolean;
    data_assinatura?: string;
    assinatura_verificada?: boolean;
    email_entregue_em?: string;
    email_recusado_em?: string;
    email_recusa_motivo?: string;
}

/** Signatário como o Autentique o conhece — usado para responder "o cliente jura
 * que assinou" quando o e-mail cadastrado não bate com ninguém no documento. */
interface SignatarioAutentique {
    email: string | null;
    nome: string | null;
    assinado_em: string | null;
}

interface DiagnosticoSync {
    numero_termo: string | null;
    situacao: string;
    signatarios: SignatarioAutentique[];
}

interface TermHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    terms: TermHistoryItem[] | null | undefined;
    clientLink?: string | null;
    /** Id da negociação — habilita o botão de sincronizar com o Autentique. */
    negociacaoId?: number | string;
    /** Recarrega a negociação após a sincronização. */
    onSynced?: () => void;
}

const fmtDataHora = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
};

/** Rótulo de assinatura de cada termo. "Aguardando verificação" é um estado real do
 * Autentique: o cliente assinou, mas a biometria do SERPRO ainda não voltou e o PDF
 * assinado não é gerado — quem confere precisa distinguir isso de "assinado". */
const BADGE: Record<StatusTermo, { texto: string; classe: string; Icone: typeof ShieldCheck }> = {
    assinado: {
        texto: 'Assinado',
        classe: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800/50',
        Icone: ShieldCheck,
    },
    aguardando_verificacao: {
        texto: 'Aguardando verificação',
        classe: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800/50',
        Icone: Clock,
    },
    nao_assinado: {
        texto: 'Não assinado',
        classe: 'bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400 border-gray-200 dark:border-slate-700',
        Icone: MinusCircle,
    },
};

const TermHistoryModal: React.FC<TermHistoryModalProps> = ({
    isOpen,
    onClose,
    terms,
    clientLink,
    negociacaoId,
    onSynced
}) => {
    const { addToast } = useToast();
    const { user } = useAuth();
    const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
    const [syncing, setSyncing] = useState(false);
    const [diagnostico, setDiagnostico] = useState<DiagnosticoSync[] | null>(null);

    // Do mais novo para o mais antigo, já com "é o último assinado" e "é o termo
    // que vale" resolvidos pela mesma regra que a análise cadastral usa.
    const linhas = useMemo(
        () => descreverHistoricoTermos(Array.isArray(terms) ? terms : []),
        [terms]
    );
    const totalAssinados = linhas.filter(l => l.status !== 'nao_assinado').length;

    const handleCopyLink = async (index: number) => {
        if (!clientLink) return;
        try {
            await navigator.clipboard.writeText(clientLink);
        } catch {
            const textarea = document.createElement('textarea');
            textarea.value = clientLink;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
        }
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2500);
    };

    const handleSync = async () => {
        if (!negociacaoId) return;
        setSyncing(true);
        setDiagnostico(null);
        try {
            const { data, error } = await supabase.functions.invoke('reconciliar-termo-adesao', {
                body: { modo: 'sync', negociacao_id: Number(negociacaoId) }
            });
            if (error) throw error;
            if (data?.success === false) throw new Error(data.error || 'Falha na sincronização.');

            const item = data?.itens?.[0];

            // Quando um termo não consta assinado pelo e-mail cadastrado, mostrar QUEM
            // o Autentique tem no documento. É o que resolve o caso "o cliente disse
            // que assinou": quase sempre ele assinou sob outro e-mail, e sem esta lista
            // isso é indistinguível de "não assinou".
            const comSignatarios: DiagnosticoSync[] = (item?.termos || [])
                .filter((t: any) => Array.isArray(t?.signatarios) && t.signatarios.some((s: SignatarioAutentique) => s.assinado_em))
                .map((t: any) => ({ numero_termo: t.numero_termo ?? null, situacao: t.situacao, signatarios: t.signatarios }));
            if (comSignatarios.length > 0) setDiagnostico(comSignatarios);

            addToast(
                item?.mudou
                    ? `Assinaturas atualizadas pelo Autentique. Termo válido: ${item.termo_operante || '—'}.`
                    : 'Tudo certo — o histórico já estava igual ao Autentique.',
                'success'
            );
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'sincronizar_termo_autentique', origem: 'acao' });
            onSynced?.();
        } catch (e: any) {
            console.error('Erro ao sincronizar termos com o Autentique:', e);
            addToast('Erro ao sincronizar com o Autentique: ' + (e?.message || 'erro desconhecido'), 'error');
        } finally {
            setSyncing(false);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in" {...propsFundoModal(() => onClose())}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-100 dark:border-slate-700/50 bg-gray-50/50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-500 rounded-lg border border-orange-100 dark:border-orange-800/50">
                            <FileText size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 font-poppins">Histórico de Termos de Adesão</h2>
                            <p className="text-sm text-gray-500 dark:text-slate-400">
                                {linhas.length} termo{linhas.length === 1 ? '' : 's'} gerado{linhas.length === 1 ? '' : 's'}
                                {totalAssinados > 0 && ` · ${totalAssinados} com assinatura do cliente`}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {negociacaoId && (
                            <button
                                onClick={handleSync}
                                disabled={syncing}
                                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800 hover:text-primary transition-colors disabled:opacity-60"
                                title="Reconsulta o Autentique e corrige quem assinou o quê"
                            >
                                {syncing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                                {syncing ? 'Sincronizando…' : 'Sincronizar'}
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6">
                    {linhas.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-gray-100 dark:border-slate-700/50 rounded-xl bg-gray-50/30 dark:bg-slate-800/30">
                            <div className="w-12 h-12 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center text-gray-400 dark:text-slate-500 mb-3">
                                <AlertCircle size={24} />
                            </div>
                            <h3 className="text-gray-900 dark:text-slate-100 font-medium">Nenhum termo encontrado</h3>
                            <p className="text-sm text-gray-500 dark:text-slate-400 max-w-xs mt-1">Este cliente ainda não possui termos de adesão gerados.</p>
                        </div>
                    ) : (
                        <>
                            {linhas.length > 1 && totalAssinados === 0 && (
                                <div className="flex gap-2 items-start mb-4 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800/40">
                                    <AlertCircle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                                    <p className="text-xs leading-relaxed text-amber-800 dark:text-amber-300">
                                        Nenhum destes termos tem assinatura registrada. Se o cliente já assinou, use <strong>Sincronizar</strong> para reconsultar o Autentique.
                                    </p>
                                </div>
                            )}

                            {/* Alguém assinou o documento no Autentique, mas não com o e-mail do
                                cadastro — por isso o termo aparece como não assinado. */}
                            {diagnostico && (
                                <div className="mb-4 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800/40">
                                    <div className="flex gap-2 items-start">
                                        <Users size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                                        <p className="text-xs leading-relaxed text-amber-800 dark:text-amber-300">
                                            O Autentique tem assinatura neste documento, mas <strong>não sob o e-mail cadastrado na negociação</strong> —
                                            por isso o termo continua como não assinado. Confira quem assinou e, se for o cliente,
                                            corrija o e-mail do cadastro e sincronize de novo.
                                        </p>
                                    </div>
                                    <div className="mt-2 pl-6 space-y-1.5">
                                        {diagnostico.map(({ numero_termo, situacao, signatarios }) => (
                                            <div key={`${numero_termo}-${situacao}`} className="text-[11px]">
                                                <span className="font-mono font-bold text-amber-900 dark:text-amber-200">{numero_termo || 'termo sem número'}</span>
                                                <span className="text-amber-700/80 dark:text-amber-400/80"> · {situacao}</span>
                                                <ul className="mt-1 space-y-0.5">
                                                    {signatarios.map((s, i) => (
                                                        <li key={`${s.email}-${i}`} className="text-amber-800 dark:text-amber-300">
                                                            {s.nome || 'sem nome'} — <span className="font-mono">{s.email || 'sem e-mail'}</span>
                                                            {s.assinado_em
                                                                ? <strong> · assinou em {fmtDataHora(s.assinado_em)}</strong>
                                                                : ' · não assinou'}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="overflow-x-auto border border-gray-200 dark:border-slate-700 rounded-xl shadow-sm">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                                    <thead className="bg-gray-50 dark:bg-slate-800/50">
                                        <tr>
                                            <th scope="col" className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                                                Número do termo
                                            </th>
                                            <th scope="col" className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                                                Gerado em
                                            </th>
                                            <th scope="col" className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                                                Assinatura do cliente
                                            </th>
                                            <th scope="col" className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                                                Tipo
                                            </th>
                                            {clientLink && (
                                                <th scope="col" className="px-5 py-3 text-center text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                                                    Link do Cliente
                                                </th>
                                            )}
                                            <th scope="col" className="px-5 py-3 text-right text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                                                Ação
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-slate-700">
                                        {linhas.map(({ termo, ordemEmissao, status, ehUltimoAssinado, ehTermoValido }, index) => {
                                            const badge = BADGE[status];
                                            const BadgeIcon = badge.Icone;
                                            return (
                                                <tr
                                                    key={`${termo.numero_termo}-${termo.data_criacao}-${index}`}
                                                    className={`transition-colors group ${ehTermoValido
                                                        ? 'bg-green-50/50 dark:bg-green-900/10 hover:bg-green-50 dark:hover:bg-green-900/20'
                                                        : 'hover:bg-gray-50 dark:hover:bg-slate-800/50'
                                                        }`}
                                                >
                                                    <td className="px-5 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <FileText size={16} className="text-gray-400 dark:text-slate-500 flex-shrink-0" />
                                                            <span className="text-sm font-mono text-gray-600 dark:text-slate-300 font-medium whitespace-nowrap">
                                                                {termo.numero_termo || 'N/A'}
                                                            </span>
                                                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400 whitespace-nowrap">
                                                                {ordemEmissao}º
                                                            </span>
                                                        </div>
                                                        {/* Marcadores do que este termo é HOJE: o último que o cliente
                                                            assinou e/ou o que a validação de dados está usando. */}
                                                        {(ehUltimoAssinado || ehTermoValido) && (
                                                            <div className="flex flex-wrap items-center gap-1.5 mt-1.5 pl-6">
                                                                {ehUltimoAssinado && (
                                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border bg-green-600 text-white border-green-600">
                                                                        <ShieldCheck size={11} /> Último assinado
                                                                    </span>
                                                                )}
                                                                {ehTermoValido && (
                                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-700">
                                                                        <ClipboardCheck size={11} /> Enviado à validação de dados
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-5 py-4 whitespace-nowrap">
                                                        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400">
                                                            <Calendar size={14} className="text-gray-400 dark:text-slate-500" />
                                                            {fmtDataHora(termo.data_criacao)}
                                                        </div>
                                                    </td>
                                                    <td className="px-5 py-4">
                                                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border ${badge.classe}`}>
                                                            <BadgeIcon size={11} /> {badge.texto}
                                                        </span>
                                                        <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                                                            {status === 'nao_assinado' ? '—' : fmtDataHora(termo.data_assinatura)}
                                                        </div>
                                                        {/* Só faz sentido enquanto não há assinatura: se o cliente assinou,
                                                            o link chegou por outro caminho e a falha de entrega virou irrelevante. */}
                                                        {status === 'nao_assinado' && emailNaoEntregue(termo) && (
                                                            <div className="mt-1.5 max-w-[16rem] whitespace-normal">
                                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800/50">
                                                                    <MailWarning size={11} /> E-mail não entregue
                                                                </span>
                                                                <p className="text-[11px] leading-snug text-red-700 dark:text-red-400 mt-1">
                                                                    Recusado em {fmtDataHora(termo.email_recusado_em)} — o cliente nunca recebeu o link.
                                                                    Use <strong>Copiar link</strong> e envie por WhatsApp.
                                                                </p>
                                                                {termo.email_recusa_motivo && (
                                                                    <p className="text-[10px] leading-snug text-gray-500 dark:text-slate-400 mt-1 font-mono break-words">
                                                                        {termo.email_recusa_motivo}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-5 py-4 whitespace-nowrap">
                                                        <div className="flex items-center gap-1.5">
                                                            <User size={14} className="text-orange-500 dark:text-orange-400" />
                                                            <span className={`text-sm font-bold px-2 py-0.5 rounded-md border ${termo.tipo_cliente === 'PJ'
                                                                ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800/50'
                                                                : 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 border-orange-100 dark:border-orange-800/50'
                                                                }`}>
                                                                {termo.tipo_cliente || '-'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    {clientLink && (
                                                        <td className="px-5 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                            <button
                                                                onClick={() => handleCopyLink(index)}
                                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all shadow-sm border ${copiedIndex === index
                                                                    ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700 text-green-700 dark:text-green-400'
                                                                    : 'bg-primary/5 dark:bg-primary/10 border-primary/20 dark:border-primary/30 text-primary hover:bg-primary/10 dark:hover:bg-primary/20'
                                                                    }`}
                                                                title="Copiar link que o cliente recebe"
                                                            >
                                                                {copiedIndex === index ? <CheckCheck size={13} /> : <Link2 size={13} />}
                                                                {copiedIndex === index ? 'Copiado!' : 'Copiar link'}
                                                            </button>
                                                        </td>
                                                    )}
                                                    <td className="px-5 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                        <a
                                                            href={termo.url_pdf}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 hover:border-gray-300 dark:hover:border-slate-600 hover:text-primary transition-all shadow-sm group-hover:border-primary/30"
                                                        >
                                                            <span className="text-xs font-bold">Acessar termo</span>
                                                            <ExternalLink size={12} />
                                                        </a>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-3 leading-relaxed">
                                A ordem é do mais recente para o mais antigo; o número (1º, 2º…) é a ordem de emissão.
                                O termo que vale é o <strong>último efetivamente assinado</strong> — é ele que vai para a validação de dados.
                                <strong> Aguardando verificação</strong> significa que o cliente assinou mas a biometria do SERPRO ainda não retornou no Autentique.
                                <strong> E-mail não entregue</strong> significa que o servidor do cliente recusou o convite do Autentique — não adianta cobrar a assinatura, o link precisa ser enviado por outro canal.
                            </p>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-800/80 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 rounded-lg text-sm font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors shadow-sm"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default TermHistoryModal;
